import type { CalendarEventRow, CompanySettings, TimeoffKind, TimeoffRequestRow, TimeoffSpan, TimeoffStatus } from '@routeback/core';

/**
 * Time off — pure date logic shared by the calendar, the request form and
 * the tests. Dates are `YYYY-MM-DD` strings throughout; arithmetic is done in
 * UTC so a phone's time zone or daylight saving never shifts a day.
 */

export const STATUS_LABEL: Record<TimeoffStatus, string> = {
  pending: 'Waiting for approval',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Cancelled',
};

export const SPAN_LABEL: Record<TimeoffSpan, string> = {
  week: 'Full week',
  days: 'Single day(s)',
  open: 'Open request',
};

export const KIND_LABEL: Record<TimeoffKind, string> = {
  vacation: 'Vacation',
  personal: 'Personal',
  sick: 'Sick',
  unpaid: 'Unpaid',
  other: 'Other',
};

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

/** Peak season: every FedEx ISP blacks out November and December unless the owner changes it. */
export const DEFAULT_BLACKOUT_MONTHS = [11, 12];

/** A person shown as off on the team calendar (from `timeoff_calendar`). */
export interface OffEntry {
  request_id: string;
  user_id: string | null;
  /** First name, or '' when the company hides names from drivers. */
  first_name: string;
  start_date: string;
  end_date: string;
}

// ── dates ─────────────────────────────────────────────────────────────────
const toUtc = (d: string): number => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
export const addDays = (d: string, n: number): string => fromUtc(toUtc(d) + n * 86_400_000);
export const daysBetween = (a: string, b: string): number => Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
/** 0 = Sunday … 6 = Saturday */
export const weekday = (d: string): number => new Date(toUtc(d)).getUTCDay();
export const monthOf = (d: string): number => +d.slice(5, 7);

/** Today's date where the company is (falls back to the phone's zone). */
export function todayIn(timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  }
}

/** The Driver Portal's "Week Starting" rule: a work week is the start day plus four. */
export const weekEnd = (start: string): string => addDays(start, 4);

/** "Mon, Oct 19" / "Oct 19 – 23" / "Oct 30 – Nov 3" / with the year when it isn't this year. */
export function formatRange(start: string | null, end: string | null, thisYear = new Date().getFullYear()): string {
  if (!start) return '';
  const e = end ?? start;
  const fmt = (d: string, o: Intl.DateTimeFormatOptions) => new Date(toUtc(d)).toLocaleDateString('en-US', { timeZone: 'UTC', ...o });
  const yr = (d: string) => (+d.slice(0, 4) !== thisYear ? `, ${d.slice(0, 4)}` : '');
  if (e === start) return fmt(start, { weekday: 'short', month: 'short', day: 'numeric' }) + yr(start);
  const sameMonth = start.slice(0, 7) === e.slice(0, 7);
  const left = fmt(start, { month: 'short', day: 'numeric' }) + (start.slice(0, 4) !== e.slice(0, 4) ? yr(start) : '');
  const right = sameMonth ? fmt(e, { day: 'numeric' }) : fmt(e, { month: 'short', day: 'numeric' });
  return `${left} – ${right}${yr(e)}`;
}

/** What a request asks for, in one line. */
export function describeRequest(r: Pick<TimeoffRequestRow, 'span' | 'start_date' | 'end_date' | 'open_request'>): string {
  if (r.span === 'open') return r.open_request;
  return formatRange(r.start_date, r.end_date);
}

/** Days in a request (inclusive), 0 for an open request. */
export function dayCount(r: Pick<TimeoffRequestRow, 'start_date' | 'end_date'>): number {
  if (!r.start_date || !r.end_date) return 0;
  return daysBetween(r.start_date, r.end_date) + 1;
}

/** Sunday-first weeks covering a month; days outside it are null. */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  const days = daysBetween(first, month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`);
  const cells: (string | null)[] = Array(weekday(first)).fill(null);
  for (let i = 0; i < days; i++) cells.push(addDays(first, i));
  while (cells.length % 7) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export function monthRange(year: number, month: number): { from: string; to: string } {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const next = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
  return { from, to: addDays(next, -1) };
}

export const overlaps = (aStart: string, aEnd: string, bStart: string, bEnd: string): boolean => aStart <= bEnd && aEnd >= bStart;

// ── holidays the Driver Portal marked on its calendar ─────────────────────
function nthWeekday(year: number, month: number, dow: number, n: number): string {
  let d = `${year}-${String(month).padStart(2, '0')}-01`;
  while (weekday(d) !== dow) d = addDays(d, 1);
  return addDays(d, 7 * (n - 1));
}
function lastWeekday(year: number, month: number, dow: number): string {
  let d = addDays(month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`, -1);
  while (weekday(d) !== dow) d = addDays(d, -1);
  return d;
}
/** FedEx Ground's six holidays (what the plugin showed). */
export function holidays(year: number): Record<string, string> {
  return {
    [`${year}-01-01`]: "New Year's Day",
    [lastWeekday(year, 5, 1)]: 'Memorial Day',
    [`${year}-07-04`]: 'Independence Day',
    [nthWeekday(year, 9, 1, 1)]: 'Labor Day',
    [nthWeekday(year, 11, 4, 4)]: 'Thanksgiving',
    [`${year}-12-25`]: 'Christmas Day',
  };
}

// ── company rules ─────────────────────────────────────────────────────────
export function blackoutMonths(settings: CompanySettings): number[] {
  const v = settings['timeoff_blackout_months'];
  return Array.isArray(v) ? v.filter((m): m is number => typeof m === 'number' && m >= 1 && m <= 12) : DEFAULT_BLACKOUT_MONTHS;
}
export const showNames = (settings: CompanySettings): boolean => settings['timeoff_show_names'] !== false;

/** Does any day of start..end fall in a blackout month or a blackout event? */
export function blackoutFor(start: string, end: string, months: number[], events: CalendarEventRow[]): string[] {
  const out: string[] = [];
  const hitMonths = new Set<number>();
  for (let d = start, i = 0; d <= end && i < 400; d = addDays(d, 1), i++) hitMonths.add(monthOf(d));
  const named = months.filter((m) => hitMonths.has(m)).map((m) => MONTHS[m - 1]!);
  if (named.length) out.push(`${named.join(' and ')} ${named.length === 1 ? 'is a blackout month' : 'are blackout months'}`);
  for (const e of events) {
    if (!e.deleted_at && e.kind === 'blackout' && overlaps(start, end, e.start_date, e.end_date)) out.push(`${e.title || 'Blackout'} (${formatRange(e.start_date, e.end_date)})`);
  }
  return out;
}

/** Who else is already approved off during start..end (first names, '' = hidden). */
export function conflictsFor(start: string, end: string, off: OffEntry[], me: string): OffEntry[] {
  const seen = new Set<string>();
  return off.filter((o) => {
    if (o.user_id === me || !overlaps(start, end, o.start_date, o.end_date) || seen.has(o.request_id)) return false;
    seen.add(o.request_id);
    return true;
  });
}

/** "Sam and Pat are" / "Someone is" / "2 people are" already off. */
export function whoIsOff(list: OffEntry[]): string {
  const names = [...new Set(list.map((o) => o.first_name).filter(Boolean))];
  if (names.length === list.length && names.length) {
    const joined = names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return `${joined} ${names.length === 1 ? 'is' : 'are'}`;
  }
  return list.length === 1 ? 'Someone is' : `${list.length} people are`;
}

/** Checks the form runs before sending. Returns a message, or null when it's fine. */
export function validate(f: { span: TimeoffSpan; start: string; end: string; openText: string; backup: string }, today: string): string | null {
  if (f.span === 'open') return f.openText.trim().length < 3 ? 'Say what you’re hoping for, e.g. “any week in August”.' : null;
  if (!f.start) return f.span === 'week' ? 'Pick the week you want off.' : 'Pick the day you want off.';
  if (!f.end || f.end < f.start) return 'The last day can’t be before the first.';
  if (f.start < today) return 'That date has already passed.';
  if (daysBetween(f.start, f.end) > 60) return 'That’s more than two months — talk to your manager about a leave.';
  if (f.backup && f.backup < today) return 'The backup week has already passed.';
  return null;
}
